import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ListBoxChangeEventArgs, ListBoxComponent, ListBoxModule } from '@syncfusion/ej2-angular-dropdowns';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { AdjustSessionEndTimeDTO, AdjustSessionStartTimeDTO, IParkingSessionDTO, ParkingSessionClient } from 'src/app/base/models/models.swagger.generated';
import { AdjustParkerRegistrationEvaluatedFunctionType } from './adjust-parker-registration-evaluated-function.type';
import { AdjustStartTimeFormComponent } from './adjust-start-time-form/adjust-start-time-form.component';
import { AdjustEndTimeFormComponent } from './adjust-end-time-form/adjust-end-time-form.component';
import { AdjustEndTimeFormGroup, AdjustEndTimeFormType } from './adjust-end-time-form/adjust-end-time-form.type';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AdjustStartTimeFormGroup, AdjustStartTimeFormType } from './adjust-start-time-form/adjust-start-time-form.type';
import { map, Observable, throwError } from 'rxjs';
import { DateTime } from 'luxon';

interface IAdjustParkerRegistrationEvaluatedListItem {
  type: string;
  toolFunction: () => Observable<void>;
  form: FormGroup<any>;
}

@Component({
    selector: 'app-adjust-parker-registration-evaluated-dialog',
    templateUrl: './adjust-parker-registration-evaluated-dialog.component.html',
    styleUrls: ['./adjust-parker-registration-evaluated-dialog.component.scss'],
    imports: [DialogModule, TranslateModule, ListBoxModule, AdjustStartTimeFormComponent, AdjustEndTimeFormComponent]
})
export class AdjustParkerRegistrationEvaluatedDialogComponent implements OnInit {
  @ViewChild('functionDialog') public functionDialog: DialogComponent;
  @ViewChild('functionListBox') public functionListBox: ListBoxComponent;

  @Output() public functionExecuted: EventEmitter<void> = new EventEmitter<void>();

  public readonly fieldsDefinition: object = { text: "type", value: "type" };
  public readonly functionsList: IAdjustParkerRegistrationEvaluatedListItem[];

  public readonly functionType = AdjustParkerRegistrationEvaluatedFunctionType;
  public adjustEndTimeForm: AdjustEndTimeFormGroup;
  public adjustStartTimeForm: AdjustStartTimeFormGroup;
  public parkingSession: IParkingSessionDTO;

  public selectedFunction: IAdjustParkerRegistrationEvaluatedListItem;

  constructor(private fb: FormBuilder, private parkingSessionClient: ParkingSessionClient) {
    this.adjustEndTimeForm = this.fb.group<AdjustEndTimeFormType>({
      endTime: this.fb.control<Date>(null, [Validators.required])
    });

    this.adjustStartTimeForm = this.fb.group<AdjustStartTimeFormType>({
      startTime: this.fb.control<Date>(null, [Validators.required])
    });

    this.functionsList = [
      { type: AdjustParkerRegistrationEvaluatedFunctionType.AdjustStartTime, toolFunction: this.adjustStartTime.bind(this), form: this.adjustStartTimeForm },
      { type: AdjustParkerRegistrationEvaluatedFunctionType.AdjustEndTime, toolFunction: this.adjustEndTime.bind(this), form: this.adjustEndTimeForm }
    ];
  }

  ngOnInit() {
  }

  public openDialog(parkingSession: IParkingSessionDTO): void {
    this.resetForms();
    this.parkingSession = parkingSession;
    this.functionDialog.show();
  }

  public closeFunctionDialog() {
    this.functionDialog.hide();
  }

  public isFunctionSelected(functionType: AdjustParkerRegistrationEvaluatedFunctionType): boolean {
    return this.selectedFunction?.type === functionType;
  }

  public applyFunction() {
    if (this.selectedFunction) {
      this.selectedFunction.toolFunction().subscribe(() => {
        this.functionExecuted?.emit();
        this.closeFunctionDialog();
      });
    }
  }

  public listBoxValue(args: ListBoxChangeEventArgs): void {
    this.selectedFunction = args.items[0] as IAdjustParkerRegistrationEvaluatedListItem;
  }

  private adjustStartTime(): Observable<void> {
    if (this.adjustStartTimeForm.invalid) {
      return throwError(() => new Error('Invalid form'));
    }
    return this.parkingSessionClient.adjustSessionStartTime(new AdjustSessionStartTimeDTO({
      parkingSessionId: this.parkingSession.id,
      startDateTime: DateTime.fromJSDate(this.adjustStartTimeForm.value.startTime)
    })).pipe(
      map(() => null)
    );
  }

  private adjustEndTime(): Observable<void> {
    if (this.adjustEndTimeForm.invalid) {
      return throwError(() => new Error('Invalid form'));
    }
    return this.parkingSessionClient.adjustSessionEndTime(new AdjustSessionEndTimeDTO({
      parkingSessionId: this.parkingSession.id,
      endDateTime: DateTime.fromJSDate(this.adjustEndTimeForm.value.endTime)
    })).pipe(
      map(() => null)
    );
  }

  private resetForms() {
    this.adjustEndTimeForm.reset();
    this.adjustStartTimeForm.reset();
  }
}
